import React, { useState, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { flattenToAppURL } from '@plone/volto/helpers';
import { IrfDatas } from './utils/actions';
import { Checkbox } from '@designsystem/stories/02-atoms/Checkbox';
import { IrfMessages as messages } from './IrfMessages';
import { getTabTranslations } from './utils/tabTranslations';
import { InternalIrfPublicationProps } from './utils/fetchData';
import { usePeerReviewState } from './hooks/usePeerReviewState';
import { IrfErrorBoundary } from './IrfErrorBoundary';
import { Loader } from '@designsystem/stories/02-atoms/Loader';
import { Tabs } from '@designsystem/stories/03-molecules/Tabs';
import {
  IrfPublicationProps,
  RenderEntries,
  IrfAlertMessages,
  fetchData,
  useHandleResize,
  useTabs,
  useActiveTabs,
} from './index';

const IrfPublication: React.FC<IrfPublicationProps> = ({
  data,
  block,
  content,
  isEditMode,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [jsonData, setJsonData] = useState<InternalIrfPublicationProps[]>([]);
  const navRef = useRef<HTMLUListElement>(null);
  const [error, setError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [dataState, setDataState] = useState<string>('');
  const [personName, setPersonName] = useState<string | null>(null);
  const [personOrCollectionChanged, setPersonOrCollectionChanged] =
    useState<boolean>(false);

  const pagePath = content?.['@id'] ? flattenToAppURL(content['@id']) : '';
  const showOnlyPeerReviewed = !!data?.only_peer_reviewed;
  const person = data?.person || null;
  const collection = data?.collection || '';
  const selectedTypes = data?.selected_types || null;
  const excluded_publications = data?.excluded_publications || null;
  const newer_than = data?.newer_than || '1970';
  const citation_style = data?.citation_style || 'apa7th';

  const prevPersonRef = useRef(person);
  const prevCollectionRef = useRef(collection);

  useEffect(() => {
    if (
      prevPersonRef.current !== person ||
      prevCollectionRef.current !== collection
    ) {
      setPersonOrCollectionChanged(true);
      prevPersonRef.current = person;
      prevCollectionRef.current = collection;
    }
  }, [person, collection]);

  const handleResize = useHandleResize({ navRef });

  /**
   * Retrieves excluded publications.
   */
  const getExcludedPublications = (excluded_publications || '')
    .split(/[,\n]/)
    .map((url) => url.trim())
    .filter(Boolean);

  /**
   * Boolean indicating whether to display content grouped or not
   */
  const displayContentGrouped =
    data?.display_content_grouped === 'group_by_type'
      ? 'group_by_type'
      : 'no_grouping';

  /**
   * Filters publications by type code.
   */
  const { tabs, activeTab, setActiveTab } = useTabs(
    jsonData,
    selectedTypes,
    person,
    personName,
  );

  /**
   * Mapping tab keys to their German translations
   */
  const tabTranslations = getTabTranslations(intl);

  /**
   * Array of active tabs with entries.
   */
  const activeTabs = useActiveTabs(
    tabs,
    showOnlyPeerReviewed,
    getExcludedPublications,
  );

  /**
   * State to cache peer-reviewed status for different tabs
   */
  const { peerReviewedCache, togglePeerReviewed } = usePeerReviewState(
    !!data?.only_peer_reviewed,
  );

  /**
   * Handles tab change and peer review toggle.
   */
  const handleTabChange = (tabKey: string) => {
    setActiveTab(tabKey as keyof typeof tabs);
  };

  const handlePeerReviewedToggle = () => {
    togglePeerReviewed(activeTab);
  };

  /**
   * Fetch data from API.
   */
  useEffect(() => {
    fetchData(
      person,
      collection,
      citation_style,
      block,
      pagePath,
      newer_than,
      showOnlyPeerReviewed,
      setIsLoading,
      setIsProcessing,
      setError,
      setDataState,
      setJsonData,
      setPersonName,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    block,
    person,
    collection,
    citation_style,
    showOnlyPeerReviewed,
    newer_than,
  ]);

  // Effect to handle resize using ResizeObserver for better performance
  useEffect(() => {
    if (jsonData.length > 0 && navRef.current) {
      handleResize();

      const navElement = navRef.current;
      let resizeObserver: ResizeObserver | null = null;

      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => {
          handleResize();
        });
        resizeObserver.observe(navElement);
      } else {
        // Fallback to window resize listener for older browsers
        window.addEventListener('resize', handleResize);
      }

      return () => {
        if (resizeObserver) {
          resizeObserver.disconnect();
        } else {
          window.removeEventListener('resize', handleResize);
        }
      };
    }
  }, [handleResize, jsonData]);

  useEffect(() => {
    if (jsonData.length > 0) {
      dispatch(IrfDatas(jsonData));
    }
  }, [dispatch, jsonData]);

  /**
   * Handle active tab.
   */
  useEffect(() => {
    if (
      activeTabs.length > 0 &&
      !activeTabs.find(([key]) => key === activeTab)
    ) {
      setActiveTab(activeTabs[0][0] as keyof typeof tabs);
    }
  }, [activeTabs, activeTab, setActiveTab]);

  const currentTab = activeTabs.map(([index]) => index).indexOf(activeTab);

  return (
    <>
      {isEditMode &&
      personOrCollectionChanged === true &&
      (person || collection) ? (
        <IrfAlertMessages messageType="saveDatas" />
      ) : (
        <>
          <IrfErrorBoundary>
            {isLoading || isProcessing ? (
              <Loader
                message={
                  !isLoading || isProcessing
                    ? intl.formatMessage(messages.processing_message)
                    : null
                }
              />
            ) : (
              <>
                {jsonData.length ? (
                  <div className="listing listing--publication listing--list">
                    <nav
                      className="listing__header"
                      aria-label="Publication Tabs"
                    >
                      <Tabs
                        items={activeTabs.map(([tabKey]) => ({
                          label: tabTranslations[tabKey as keyof typeof tabs],
                          controls: `${tabKey}-panel`,
                        }))}
                        currentTab={currentTab}
                        onTabChange={(i) => {
                          handleTabChange(
                            activeTabs[i][0] as keyof typeof tabs,
                          );
                        }}
                        enableScroll={true}
                      />
                    </nav>

                    {(activeTab === 'publicationTab' ||
                      activeTab === 'presentationTab') &&
                      showOnlyPeerReviewed === false && (
                        <div className="peer-reviewed__state">
                          <Checkbox
                            label={intl.formatMessage(
                              messages.only_peer_reviewed_switch,
                            )}
                            name="peerReviewed"
                            onChange={handlePeerReviewedToggle}
                            checked={peerReviewedCache[activeTab]}
                            aria-label={intl.formatMessage(
                              messages.only_peer_reviewed_switch,
                            )}
                          />
                        </div>
                      )}
                    <div
                      role="tabpanel"
                      className="listing__contents"
                      id={`${activeTab}-panel`}
                    >
                      <RenderEntries
                        entries={tabs[activeTab]}
                        peerReviewedCache={peerReviewedCache}
                        activeTab={activeTab}
                        showOnlyPeerReviewed={showOnlyPeerReviewed}
                        getExcludedPublications={getExcludedPublications}
                        isEditMode={!!isEditMode}
                        dataState={dataState || ''}
                        jsonData={jsonData}
                        displayContentGrouped={displayContentGrouped}
                      />
                    </div>
                  </div>
                ) : (
                  !(person || collection) && (
                    <IrfAlertMessages messageType="selectCollection" />
                  )
                )}

                {error === true && (person || collection) && (
                  <IrfAlertMessages messageType="serverError500" />
                )}
              </>
            )}
          </IrfErrorBoundary>
        </>
      )}
    </>
  );
};

export default IrfPublication;
